"use client";

import { useEffect, useCallback } from "react";
import { useTranslations } from "next-intl";
import { motion, AnimatePresence } from "framer-motion";
import { X, Book, Sparkles, MessageCircle, ExternalLink } from "lucide-react";
import { useHelp } from "@/contexts/HelpContext";
import {
  CHANGELOG_ENTRIES,
  DOCS_URL,
  CHANGELOG_URL,
} from "@/constants/changelog";

const typeColors = {
  feature: "bg-emerald-100 text-emerald-600",
  improvement: "bg-blue-100 text-blue-600",
  fix: "bg-amber-100 text-amber-600",
};

export function HelpPanel() {
  const t = useTranslations("help");
  const { isOpen, closeHelp } = useHelp();

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === "Escape") closeHelp();
    },
    [isOpen, closeHelp],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleSupportClick = () => {
    if (typeof window !== "undefined" && window.Intercom) {
      window.Intercom("showNewMessage");
    }
    closeHelp();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={closeHelp}
          />

          {/* Panel */}
          <motion.div
            initial={{ opacity: 0, x: 400 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 400 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-[400px] h-full bg-white border-l border-[var(--border)] shadow-[0_24px_48px_-12px_rgba(0,0,0,0.18)] overflow-y-auto"
          >
            {/* Header */}
            <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-white border-b border-[var(--border)]">
              <h2 className="text-lg font-semibold text-black lowercase font-['MuseoModerno']">
                {t("title")}
              </h2>
              <button
                onClick={closeHelp}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[var(--surface)] transition-colors"
              >
                <X className="w-5 h-5 text-[var(--foreground-muted)]" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 pt-8 space-y-8">
              {/* Documentation Card */}
              <a
                href={DOCS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="block p-6 rounded-xl border border-[var(--border)] hover:border-[#ff8086]/50 hover:bg-[#ff8086]/5 transition-all duration-200 group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#ff8086]/10 flex items-center justify-center flex-shrink-0">
                    <Book className="w-6 h-6 text-[#ff8086]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-semibold text-black lowercase">
                        {t("documentation")}
                      </h3>
                      <ExternalLink className="w-4 h-4 text-[var(--foreground-muted)] opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <p className="text-sm text-[var(--foreground-muted)] mt-1.5 lowercase">
                      {t("documentationSubtitle")}
                    </p>
                  </div>
                </div>
              </a>

              {/* Changelog Section */}
              <div className="rounded-xl border border-[var(--border)] overflow-hidden">
                <div className="px-6 py-5 border-b border-[var(--border)] flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="text-base font-semibold text-black lowercase">
                    {t("whatsNew")}
                  </h3>
                </div>

                <div className="divide-y divide-[var(--border)]">
                  {CHANGELOG_ENTRIES.slice(0, 4).map((entry, index) => (
                    <div
                      key={index}
                      className="px-6 py-4 flex items-center gap-3"
                    >
                      <span
                        className={`text-[10px] font-medium px-2 py-1 rounded lowercase ${typeColors[entry.type]}`}
                      >
                        {entry.type}
                      </span>
                      <span className="text-sm text-black lowercase flex-1 truncate">
                        {entry.title}
                      </span>
                    </div>
                  ))}
                </div>

                <a
                  href={CHANGELOG_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-6 py-4 text-sm font-medium text-[#ff8086] hover:bg-[#ff8086]/5 transition-colors lowercase flex items-center gap-1.5 border-t border-[var(--border)]"
                >
                  {t("viewAllUpdates")}
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>

              {/* Contact Support Section */}
              <div className="rounded-xl border border-[var(--border)] p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-black lowercase">
                      {t("contactSupport")}
                    </h3>
                    <p className="text-sm text-[var(--foreground-muted)] mt-1.5 lowercase">
                      {t("contactSupportSubtitle")}
                    </p>
                  </div>
                </div>

                <button
                  onClick={handleSupportClick}
                  className="mt-5 w-full px-4 py-3 rounded-xl text-sm font-medium text-white bg-[#ff8086] hover:bg-[#e66b70] transition-all duration-200 active:scale-[0.98] shadow-[0_2px_8px_rgba(255,128,134,0.3)] lowercase"
                >
                  {t("chatWithUs")}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
